/**
 * Determines the target route for a notification click, ensuring the user
 * has appropriate permissions/roles for the target area.
 *
 * @param {Object} notification - The notification object
 * @param {Object} user - The current user object from auth/localStorage
 * @param {boolean} isSystemAdmin - Whether the current user is a SystemAdmin
 * @returns {string} The permitted target route, or '/notifications' fallback
 */
export function getNotificationNavigationPath(notification, user = null, isSystemAdmin = false) {
  if (!notification) return '/notifications';

  const type = notification.type || notification.Type;
  const refId = notification.referenceId || notification.ReferenceId;

  const isTribunalModerator = Boolean(user?.isTribunalModerator) || isSystemAdmin;
  const isGuildAdmin = Boolean(user?.isGuildAdmin) || user?.role === 'GuildAdmin' || isSystemAdmin;
  const isPartner = user?.role === 'Partner' || isSystemAdmin;

  switch (type) {
    case 'DirectChat':
      return refId ? `/chats?userId=${encodeURIComponent(refId)}` : '/chats';

    case 'GuildInvite':
    case 'GuildRoleChange':
    case 'GuildApplication':
    case 'Guild':
      return refId ? `/guilds/${refId}` : '/guilds';

    case 'GuildHeroicActionInvited':
      return refId ? `/guilds/${refId.split(':')[0]}/bounties` : '/guilds';

    case 'GuildHeroicActionAnnounced':
    case 'GuildHeroicActionCancelled':
    case 'GuildHeroicActionApproved':
    case 'GuildHeroicActionRejected':
      return '/guilds';

    case 'HeroicActionApproved':
    case 'HeroicActionRejected':
    case 'HeroicActionReturned':
    case 'HeroicActionAppealApproved':
    case 'HeroicActionAppealRejected':
      return '/heroic-actions';
    case 'HeroicActionAppealPending':
      return isSystemAdmin ? '/admin/heroic-actions' : '/notifications';
    case 'HeroicActionReviewAssigned':
      return isTribunalModerator ? '/tribunal' : '/notifications';

    case 'GuildJoinRequest':
      return refId ? `/guilds/${refId}` : '/guilds';

    case 'EventStarted':
    case 'Event':
      return '/tavern';

    case 'ModeratorApproved':
    case 'ModeratorRejected':
    case 'ModeratorApplication':
    case 'Tribunal':
      // Permission check: Requires Tribunal Moderator or SystemAdmin rights
      return type === 'ModeratorApplication'
        ? (isSystemAdmin ? '/admin/heroic-actions' : '/notifications')
        : (isTribunalModerator ? '/tribunal' : '/notifications');

    case 'Promotion':
      return '/profile';

    case 'GuildAdmin':
      // Permission check: Requires GuildAdmin or SystemAdmin rights
      return isGuildAdmin ? (user?.guildId || refId ? `/guilds/${user?.guildId || refId}` : '/guilds') : '/notifications';

    case 'Partner':
      // Permission check: Requires Partner or SystemAdmin rights
      return isPartner ? '/partner' : '/notifications';

    default:
      return '/notifications';
  }
}
